import {
    enrichAssetsWithGroupSaleInfo,
    isAssetSold,
    formatAssetDescription,
} from './assetSaleDisplayUtils';

const formatDateShort = (d) =>
    !d ? '-' : new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });

/** All sold assets with relation/group sale enrichment applied. */
export function getSoldAssetsForDisplay(assets, assetRelations) {
    const enriched = enrichAssetsWithGroupSaleInfo(assets, assetRelations);
    return enriched.filter((a) => isAssetSold(a));
}

/** Exclude relation children that inherit sale from root (no standalone sale row). */
export function filterSoldAssetsForCapitalGain(soldAssets) {
    return (soldAssets || []).filter((a) => !a.soldViaAssetGroup);
}

/** Subtle highlight for a sold root that has linked children (matches app table tones). */
export const SOLD_ASSET_ROOT_GROUP_BG = 'rgba(48, 79, 255, 0.06)';
/** Child rows under a root — neutral grey like SoldAssetsViewer / search fields. */
export const SOLD_ASSET_CHILD_ROW_BG = '#F5F5F6';
export const SOLD_ASSET_ROW_HOVER_BG = 'rgba(191, 200, 255, 0.28)';

export const getRelationChildIds = (rel) => {
    const rootId = rel?.rootAssetId || rel?.rootAsset?.id;
    const ids =
        Array.isArray(rel?.relatedAssetIds) && rel.relatedAssetIds.length > 0
            ? rel.relatedAssetIds
            : (rel?.relatedAssets || []).map((c) => c?.id).filter(Boolean);
    return ids.filter((id) => id && id !== rootId);
};

/**
 * Builds maps for sold-asset relation display: all child ids, and sold children grouped under sold roots.
 */
export function buildSoldAssetRelationMaps(soldAssets, assetRelations) {
    const childAssetIdSet = new Set();
    const rootToChildren = {};
    const byId = new Map();
    (soldAssets || []).forEach((a) => {
        if (a?.id) byId.set(a.id, a);
    });

    (assetRelations || []).forEach((rel) => {
        const rootId = rel?.rootAssetId || rel?.rootAsset?.id;
        if (!rootId) return;

        const childIds = getRelationChildIds(rel);
        childIds.forEach((id) => childAssetIdSet.add(id));

        const root = byId.get(rootId);
        if (!root || !isAssetSold(root)) return;

        const children = childIds
            .map((id) => byId.get(id))
            .filter((c) => c && isAssetSold(c))
            .sort((a, b) => {
                const an = parseInt(a?.assetNumber, 10);
                const bn = parseInt(b?.assetNumber, 10);
                if (!Number.isNaN(an) && !Number.isNaN(bn)) return an - bn;
                return String(a?.assetNumber || '').localeCompare(String(b?.assetNumber || ''));
            });

        if (children.length > 0) {
            rootToChildren[rootId] = children;
        }
    });

    return { childAssetIdSet, rootToChildren };
}

/** Top-level sold rows: roots and standalone assets (excludes relation children). */
export function getTopLevelSoldAssets(soldAssets, childAssetIdSet) {
    return (soldAssets || []).filter((a) => a?.id && !childAssetIdSet.has(a.id));
}

export function assetMatchesSoldSearch(asset, searchTerm, groupMap) {
    if (!searchTerm?.trim() || !asset) return true;
    const q = searchTerm.trim().toLowerCase();
    const displayDescription = formatAssetDescription(asset.description, asset.sellDate, isAssetSold(asset));
    return (
        (asset.name || '').toLowerCase().includes(q) ||
        (asset.assetNumber?.toString() || '').includes(q) ||
        (displayDescription || '').toLowerCase().includes(q) ||
        (groupMap[asset.assetGroupId] || '').toLowerCase().includes(q) ||
        formatDateShort(asset.sellDate).includes(q)
    );
}

/** Search on top-level assets; include root when any linked sold child matches. */
export function filterTopLevelSoldWithChildSearch(topLevelAssets, rootToChildren, searchTerm, groupMap) {
    if (!searchTerm?.trim()) return topLevelAssets || [];
    return (topLevelAssets || []).filter((root) => {
        if (assetMatchesSoldSearch(root, searchTerm, groupMap)) return true;
        const children = rootToChildren[root.id] || [];
        return children.some((c) => assetMatchesSoldSearch(c, searchTerm, groupMap));
    });
}

export function filterSoldAssetsBySearch(soldAssets, searchTerm, groupMap) {
    if (!searchTerm?.trim()) return soldAssets || [];
    const q = searchTerm.trim().toLowerCase();
    return (soldAssets || []).filter((a) => {
        const displayDescription = formatAssetDescription(a.description, a.sellDate, isAssetSold(a));
        return (
            (a.name || '').toLowerCase().includes(q) ||
            (a.assetNumber?.toString() || '').includes(q) ||
            (displayDescription || '').toLowerCase().includes(q) ||
            (groupMap[a.assetGroupId] || '').toLowerCase().includes(q) ||
            formatDateShort(a.sellDate).includes(q)
        );
    });
}

export function buildAssetGroupMap(assetGroups) {
    const map = {};
    (assetGroups || []).forEach((g) => {
        map[g.assetGroupId] = g.name;
    });
    return map;
}
